import * as React from "react";
import { useState } from "react";
import { Button, Input, Label, Checkbox } from "@/components/ui";
import { MfaServices } from "@/services/bedrock/MfaServices";
import { useSocialCoachSession } from "@/utils";
import { Loader2, ArrowRight, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { useSmsSetup } from "../context";

export const EnterPhone: React.FC = () => {
  const { phone, setPhone, setStep, isLoading, setIsLoading } = useSmsSetup();
  const { session } = useSocialCoachSession();
  const [acceptTos, setAcceptTos] = useState(false);

  const handleRequestSmsCode = async () => {
    if (!phone || !acceptTos) return;
    setIsLoading(true);
    try {
      const cleaned = phone.replace(/\D/g, "");
      const formattedPhone = phone.startsWith("+1") ? phone : `+1${cleaned}`;
      await MfaServices.updateMfaPhone(formattedPhone);
      await MfaServices.acceptSmsTos();
      await MfaServices.requestChallenge("sms");
      setStep("VERIFY_PHONE");
      toast.success("Verification code sent!");
    } catch (e) {
      toast.error("Failed to send code. Please check the number.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoBack = () => {
    if (!session?.mfaVerified) {
      setStep("VERIFY_IDENTITY");
    }
  };

  return (
    <div className="flex flex-col items-center justify-center py-6 space-y-6 flex-1 h-full max-w-md mx-auto animate-in fade-in duration-200">
      <div className="w-full space-y-4">
        <div className="text-center space-y-2">
          <div className="text-xl font-bold text-slate-800">Add your phone number</div>
          <p className="text-sm text-slate-500">
            To secure your account, we'll send verification codes to this number when signing in from unrecognized
            devices.
          </p>
        </div>

        <div className="space-y-2 w-full pt-2">
          <Label htmlFor="setupPhone" className="text-xs font-bold text-slate-500 uppercase tracking-wider">
            Mobile Phone Number
          </Label>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-sm select-none">
              +1
            </span>
            <Input
              id="setupPhone"
              className="pl-11 h-12 rounded-xl border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-50 text-base"
              placeholder="(555) 000-0000"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              disabled={isLoading}
            />
          </div>
          <p className="text-[11px] text-slate-400">Standard US mobile numbers only. Msg & data rates may apply.</p>
        </div>

        <div className="bg-slate-50/50 border border-slate-200/60 rounded-xl p-4 w-full">
          <div className="flex items-start space-x-3">
            <Checkbox
              id="setupSmsTos"
              className="mt-1 border-slate-300 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
              checked={acceptTos}
              onCheckedChange={checked => setAcceptTos(checked === true)}
            />
            <div className="grid gap-1 leading-none">
              <label htmlFor="setupSmsTos" className="text-sm font-semibold text-slate-700 cursor-pointer select-none">
                I agree to receive security codes via SMS
              </label>
              <div className="text-[11px] text-slate-400 leading-relaxed mt-1">
                You can opt-out at any time. Read our{" "}
                <a
                  href="https://app.socialcoach.io/terms"
                  target="_blank"
                  className="text-blue-600 text-[11px] hover:underline inline-flex items-center gap-0.5 font-medium"
                >
                  Terms <ExternalLink className="h-2.5 w-2.5" />
                </a>{" "}
                and{" "}
                <a
                  href="https://app.socialcoach.io/privacy"
                  target="_blank"
                  className="text-blue-600 hover:underline text-[11px] inline-flex items-center gap-0.5 font-medium"
                >
                  Privacy <ExternalLink className="h-2.5 w-2.5" />
                </a>{" "}
                policies.
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full space-y-3">
        <Button
          className="w-full h-12 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold rounded-xl shadow-md gap-2 hover:translate-y-[-1px] active:translate-y-[1px] transition-all disabled:opacity-50"
          onClick={handleRequestSmsCode}
          disabled={isLoading || !phone || !acceptTos}
        >
          {isLoading ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <>
              <span>Send Verification Code</span>
              <ArrowRight className="h-4 w-4" />
            </>
          )}
        </Button>

        {/* Back Button for Navigation */}
        {!session?.mfaVerified && (
          <button
            type="button"
            className="text-sm font-semibold text-slate-400 hover:text-slate-600 transition-colors block mx-auto py-1"
            onClick={handleGoBack}
            disabled={isLoading}
          >
            Go Back
          </button>
        )}
      </div>
    </div>
  );
};
